import {PageHeader} from '@primer/react/experimental'
import {useStickyHeader} from '@github-ui/use-sticky-header/useStickyHeader'
import {PullRequestStateLabel} from './PullRequestStateLabel'
import type {PullRequestState} from '../types'
import {PullRequestHeaderSummary} from './PullRequestHeaderSummary'
import type {Repository} from '@github-ui/current-repository'
import stickyStyles from '@github-ui/use-sticky-header/use-sticky-header.module.css'
import {clsx} from 'clsx'
import styles from './StickyPullRequestHeader.module.css'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {StackData} from '../page-data/payloads/header'
import {StackState} from './StackState'
import {OverlayPortalContainer} from './toolbar/OverlayPortalContainer'

export const condensedResponsiveWrapperClasses = 'container-xl tmp-px-3 tmp-px-md-4 tmp-px-lg-5'
export const fullWidthResponsiveWrapperClasses = 'tmp-px-3 tmp-px-md-4 tmp-px-lg-5'
const STACK_STATE_PORTAL_ROOT = 'stack-state-portal-root'

export interface StickyPullRequestHeaderData {
  author: {
    login: string
    displayName?: string | null
  }
  attributedUser?: {
    login: string
    displayName?: string | null
    avatarUrl?: string | null
  } | null
  baseBranch: string
  commitsCount: number
  headBranch: string
  headRepositoryOwnerLogin?: string
  headRepositoryName?: string
  id: number
  isInAdvisoryRepo: boolean
  mergedBy?: string | null
  mergedByName?: string | null
  number: number
  state: PullRequestState
  title: string
  url: string
  titleHtml: SafeHTMLString
}

export interface StickyPullRequestHeaderProps {
  viewerCanUnstack?: boolean
  repository: Repository
  pullRequest: StickyPullRequestHeaderData
  pageVariant: 'condensed' | 'full-width'
  stack?: StackData | null
}

export function StickyPullRequestHeader({
  viewerCanUnstack = false,
  repository,
  pullRequest,
  pageVariant,
  stack,
}: StickyPullRequestHeaderProps) {
  const {isSticky, sensorRef} = useStickyHeader()
  const responsiveWrapperClasses =
    pageVariant === 'condensed' ? condensedResponsiveWrapperClasses : fullWidthResponsiveWrapperClasses

  return (
    <>
      <PageHeader
        className={clsx(
          stickyStyles.stickyHeader,
          styles.prHeader,
          isSticky && styles['is-stuck'],
          'color-shadow-small',
        )}
      >
        <PageHeader.TitleArea
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className={clsx('flex-items-center justify-center', responsiveWrapperClasses, styles.prTitleArea)}
        >
          <PageHeader.LeadingVisual>
            <PullRequestStateLabel state={pullRequest.state} />
          </PageHeader.LeadingVisual>
          <PageHeader.Title className="flex-1 min-width-0 lh-default">
            <div className="d-flex flex-items-baseline flex-nowrap gap-2 mb-n1">
              <a href="#top" className="d-flex min-width-0 overflow-hidden f5 text-bold fgColor-default">
                <VerifiedHTMLText
                  className={clsx('overflow-hidden no-wrap markdown-title', styles.textEllipsis)}
                  html={pullRequest.titleHtml}
                />
              </a>
              <span className="flex-shrink-0 f5 text-normal fgColor-muted">#{pullRequest.number}</span>
            </div>
            <div className="f6 text-normal d-flex flex-items-center min-width-0 ml-n2">
              {/* Unmount when the header is no longer sticky so the overlay doesn't outlive its anchor. */}
              {isSticky && stack && (
                <>
                  {/* Keeps the overlay in the fixed header's positioning context. */}
                  <OverlayPortalContainer portalName={STACK_STATE_PORTAL_ROOT} />
                  <StackState
                    pullRequestNumber={pullRequest.number}
                    stack={stack}
                    viewerCanUnstack={viewerCanUnstack}
                    anchorVariant="compact"
                    overlayPortalContainerName={STACK_STATE_PORTAL_ROOT}
                  />
                  <div className={styles.divider} />
                </>
              )}
              <PullRequestHeaderSummary
                author={pullRequest.author}
                attributedUser={pullRequest.attributedUser}
                baseBranch={pullRequest.baseBranch}
                baseRepositoryOwnerLogin={repository.ownerLogin}
                baseRepositoryName={repository.name}
                className="flex-1 flex-nowrap min-width-0 no-wrap px-2"
                commitsCount={pullRequest.commitsCount}
                headBranch={pullRequest.headBranch}
                headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                headRepositoryName={pullRequest.headRepositoryName}
                isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                mergedBy={pullRequest.mergedBy}
                mergedByName={pullRequest.mergedByName}
                pullRequestId={pullRequest.id}
                pullRequestNumber={pullRequest.number}
                repositoryId={repository.id}
                state={pullRequest.state}
              />
            </div>
          </PageHeader.Title>
        </PageHeader.TitleArea>
      </PageHeader>
      <div ref={sensorRef} className={styles.stickyHeaderActivationThreshold} />
    </>
  )
}
